import React, { useState } from "react";
import {
  ALL_PLURAL_LABEL,
  CC_LABEL,
  CLOSE_LABEL,
  EMPTY_STRING,
  GROUP_TARGETS_LABEL,
  PRACTICE_LABEL,
  VIEW_LABEL,
} from "../../constants/Dictionary";
import { getPracticeCoverageSummary } from "../../utils/orgFunctions.utils";
import { BodyTyp } from "../Typography/Typography";
import { FontWeightEnum, IconSizeEnum } from "../../types/AppTypes";
import { useAppSelector } from "../../hooks/store.hook";
import { Button, ButtonSizes, ButtonVariants } from "../core/Button/Button";
import { IconManager } from "../core/IconManager/IconManager";
import { ReactComponent as EyeIcon } from "../../icons/eye.svg";
import { ReactComponent as CloseIcon } from "../../icons/close.svg";
import {
  StyledButtonViewContainer,
  StyledLabelAccordion,
  StyledOrgAccordionContent,
  StyledOrgAccordionOption,
  StyledTargetGroupsModalContainer,
} from "./TargetGroups.styled";
import { Modal } from "../core/Modal/Modal";
import { Accordion } from "../core/Accordion/Accordion";

interface TargetGroupsProps {
  cc: string[];
}

const TargetGroups = ({ cc }: TargetGroupsProps) => {
  const { orgSettings } = useAppSelector((state) => state.config);

  const [showTargetsModal, setShowTargetsModal] = useState(false);

  const targetList = getPracticeCoverageSummary(orgSettings.areaList.all, cc);

  const renderTargets = () => {
    if (targetList === null) {
      return EMPTY_STRING;
    }

    if (targetList.length === 0) {
      return ALL_PLURAL_LABEL;
    }

    if (targetList.length === 1 && targetList[0].cc.length === 0) {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM} noTranslate>
          {`${PRACTICE_LABEL} - ${targetList[0].practice}`}
        </BodyTyp>
      );
    }

    if (targetList.length === 1 && targetList[0].cc.length === 1) {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM} noTranslate>
          {`${CC_LABEL} | ${targetList[0].cc[0]}`}
        </BodyTyp>
      );
    }

    return (
      <StyledButtonViewContainer>
        <Button
          variant={ButtonVariants.SECONDARY}
          label={VIEW_LABEL}
          size={ButtonSizes.SMALL}
          icon={
            <IconManager marginRight={10} size={IconSizeEnum.SMALL}>
              <EyeIcon />
            </IconManager>
          }
          onClick={() => setShowTargetsModal(true)}
        />
      </StyledButtonViewContainer>
    );
  };

  const renderTargetGroups = () => {
    return (
      <StyledTargetGroupsModalContainer>
        <BodyTyp variant="1" fontWeight={FontWeightEnum.MEDIUM}>
          {orgSettings.areaList?.all[0]?.name}
        </BodyTyp>
        {targetList?.map((item) => {
          return (
            <Accordion
              key={`practice-${item.practice}`}
              label={
                <StyledLabelAccordion>
                  {item.practice}
                  {item.cc.length === 0 ? (
                    <>
                      <BodyTyp fontWeight={FontWeightEnum.REGULAR}>|</BodyTyp>
                      {
                        <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD}>
                          {ALL_PLURAL_LABEL}
                        </BodyTyp>
                      }
                    </>
                  ) : null}
                </StyledLabelAccordion>
              }
              collapsible={item.cc.length > 0}
            >
              <StyledOrgAccordionContent>
                {item.cc.map((cc) => {
                  return (
                    <StyledOrgAccordionOption key={`$cc-${cc}`}>
                      {cc}
                    </StyledOrgAccordionOption>
                  );
                })}
              </StyledOrgAccordionContent>
            </Accordion>
          );
        })}
      </StyledTargetGroupsModalContainer>
    );
  };

  return (
    <>
      <div>{renderTargets()}</div>
      <Modal
        isOpen={showTargetsModal}
        closeModal={() => setShowTargetsModal(false)}
        rightButton={
          <Button
            variant={ButtonVariants.SECONDARY}
            label={CLOSE_LABEL}
            size={ButtonSizes.REGULAR}
            icon={
              <IconManager marginRight={10} size={IconSizeEnum.SMALL}>
                <CloseIcon />
              </IconManager>
            }
            onClick={() => setShowTargetsModal(false)}
          />
        }
        label={GROUP_TARGETS_LABEL}
      >
        {renderTargetGroups()}
      </Modal>
    </>
  );
};

export default TargetGroups;
